import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuinielaStore } from '../store/quinielaStore'
import { useAuthStore } from '../store/authStore'
import { 
  Trophy, 
  Calendar, 
  Users, 
  DollarSign, 
  Clock, 
  AlertCircle,
  Loader,
  TrendingUp
} from 'lucide-react'

export default function Quinielas() {
  const { quinielas, loading, fetchAvailableQuinielas, fetchAllQuinielas, fetchQuinielasByStatus } = useQuinielaStore()
  const { user } = useAuthStore()
  const [filter, setFilter] = useState('available') // available, upcoming, finished, all

  useEffect(() => {
    console.log('Quinielas: Loading with filter:', filter)
    loadQuinielas()
  }, [filter])

  const loadQuinielas = async () => {
    console.log('Quinielas: Fetching quinielas...')
    switch (filter) {
      case 'available':
        await fetchAvailableQuinielas()
        break
      case 'upcoming':
        await fetchQuinielasByStatus('upcoming')
        break
      case 'finished':
        await fetchQuinielasByStatus('finished')
        break
      case 'all':
        await fetchAllQuinielas()
        break
      default:
        await fetchAvailableQuinielas()
    }
    console.log('Quinielas: Loaded count:', quinielas.length)
  }

  const getStatusBadge = (quiniela) => {
    const now = new Date()
    const closeDate = quiniela.registrationCloseDate?.toDate()
    const startDate = quiniela.startDate?.toDate()
    
    if (quiniela.status === 'finished') {
      return <span className="badge bg-kiniela-card text-kiniela-text">Finalizada</span>
    }
    
    if (quiniela.status === 'in_progress') {
      return <span className="badge bg-kiniela-accent text-kiniela-text">En Progreso</span>
    }
    
    if (closeDate && closeDate <= now) {
      return <span className="badge bg-kiniela-cta text-kiniela-text">Cerrada</span>
    }
    
    if (startDate && startDate <= now) {
      return <span className="badge bg-kiniela-gold text-kiniela-bg">Por Iniciar</span>
    }
    
    return <span className="badge bg-kiniela-accent text-kiniela-text">Abierta</span>
  }

  const getTimeRemaining = (closeDate) => {
    if (!closeDate) return null
    
    const now = new Date()
    const close = closeDate.toDate()
    const diff = close - now
    
    if (diff <= 0) return 'Cerrada'
    
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    
    if (hours > 24) {
      const days = Math.floor(hours / 24)
      return `${days}d ${hours % 24}h restantes`
    }
    
    return `${hours}h ${minutes}m restantes`
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate()
    return date.toLocaleDateString('es-MX', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const canJoin = (quiniela) => {
    const now = new Date()
    const closeDate = quiniela.registrationCloseDate?.toDate()
    
    if (!closeDate || closeDate <= now) return false
    if (quiniela.status === 'closed' || quiniela.status === 'finished') return false
    if (quiniela.participants.maxLimit && quiniela.participants.count >= quiniela.participants.maxLimit) return false
    
    return true
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-kiniela-bg min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-kiniela-gold mb-2">Quinielas Disponibles</h1>
        <p className="text-kiniela-text">
          Únete a una quiniela, predice los resultados y gana <span className="text-kiniela-gold">premios</span>
        </p>
      </div>

      {/* Filtros */}
      <div className="mb-6 flex gap-2 overflow-x-auto">
        <button
          onClick={() => setFilter('available')}
          className={`btn btn-secondary ${
            filter === 'available' 
              ? 'bg-kiniela-gold text-kiniela-bg border-2 border-kiniela-gold font-bold shadow-lg' 
              : 'bg-kiniela-card text-kiniela-text border border-kiniela-border hover:border-kiniela-gold'
          }`}
        >
          Disponibles
        </button>
        <button
          onClick={() => setFilter('upcoming')}
          className={`btn btn-secondary ${
            filter === 'upcoming' 
              ? 'bg-kiniela-gold text-kiniela-bg border-2 border-kiniela-gold font-bold shadow-lg' 
              : 'bg-kiniela-card text-kiniela-text border border-kiniela-border hover:border-kiniela-gold'
          }`}
        >
          En Progreso
        </button>
        <button
          onClick={() => setFilter('finished')}
          className={`btn btn-secondary ${
            filter === 'finished' 
              ? 'bg-kiniela-gold text-kiniela-bg border-2 border-kiniela-gold font-bold shadow-lg' 
              : 'bg-kiniela-card text-kiniela-text border border-kiniela-border hover:border-kiniela-gold'
          }`}
        >
          Finalizadas
        </button>
        <button
          onClick={() => setFilter('all')}
          className={`btn btn-secondary ${
            filter === 'all' 
              ? 'bg-kiniela-gold text-kiniela-bg border-2 border-kiniela-gold font-bold shadow-lg' 
              : 'bg-kiniela-card text-kiniela-text border border-kiniela-border hover:border-kiniela-gold'
          }`}
        >
          Todas
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <Loader className="animate-spin text-kiniela-muted" size={40} />
        </div>
      )}

      {/* Estado vacío */}
      {!loading && quinielas.length === 0 && (
        <div className="card text-center py-12">
          <Trophy className="mx-auto mb-4 text-kiniela-gold" size={48} />
          <h3 className="text-xl font-semibold text-kiniela-gold mb-2">
            No hay quinielas disponibles
          </h3>
          <p className="text-kiniela-text">
            Vuelve pronto para ver las quinielas en progreso
          </p>
        </div>
      )}

      {/* Lista de Quinielas */}
      {!loading && quinielas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quinielas.map((quiniela) => (
            <div key={quiniela.id} className={`card ${quiniela.prizePool > 0 ? 'card-premium' : ''} hover:shadow-lg transition-shadow`}>
              {/* Header */}
              <div className="mb-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-bold text-kiniela-gold flex-1">
                    {quiniela.name}
                  </h3>
                  {getStatusBadge(quiniela)}
                </div>
                {quiniela.description && (
                  <p className="text-sm text-kiniela-muted">{quiniela.description}</p>
                )}
              </div>
              {/* Stats */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-kiniela-muted flex items-center gap-2">
                    <Calendar size={16} />
                    Inicio
                  </span>
                  <span className="font-medium text-kiniela-text">
                    {formatDate(quiniela.startDate)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-kiniela-muted flex items-center gap-2">
                    <Clock size={16} />
                    Cierre
                  </span>
                  <span className="font-medium text-kiniela-gold">
                    {getTimeRemaining(quiniela.registrationCloseDate)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-kiniela-muted flex items-center gap-2">
                    <Users size={16} />
                    Participantes
                  </span>
                  <span className="font-medium text-kiniela-text">
                    {quiniela.participants.count}
                    {quiniela.participants.maxLimit && ` / ${quiniela.participants.maxLimit}`}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-kiniela-muted flex items-center gap-2">
                    <Trophy size={16} />
                    Partidos
                  </span>
                  <span className="font-medium text-kiniela-text">
                    {quiniela.totalMatches}
                  </span>
                </div>
              </div>
              {/* Entry Fee & Prize Pool */}
              <div className="border-t border-kiniela-border pt-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-kiniela-muted">Entrada</span>
                  <span className="text-xl font-bold text-kiniela-text">
                    ${quiniela.entryFee.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-kiniela-muted flex items-center gap-1">
                    <TrendingUp size={14} />
                    Bolsa Acumulada
                  </span>
                  <span className="text-lg font-bold text-kiniela-gold">
                    ${quiniela.prizePool.toFixed(2)}
                  </span>
                </div>
              </div>
              {/* Prizes Distribution */}
              {quiniela.prizePool > 0 && (
                <div className="bg-kiniela-card rounded-lg p-3 mb-4 border border-kiniela-gold">
                  <p className="text-xs text-kiniela-gold mb-2">Premios Actuales:</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xs text-kiniela-gold">1°</p>
                      <p className="text-sm font-bold text-kiniela-gold">
                        ${quiniela.prizes.first.toFixed(0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-kiniela-text">2°</p>
                      <p className="text-sm font-bold text-kiniela-text">
                        ${quiniela.prizes.second.toFixed(0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-kiniela-muted">3°</p>
                      <p className="text-sm font-bold text-kiniela-muted">
                        ${quiniela.prizes.third.toFixed(0)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Action Button */}
              <Link
                to={`/quinielas/${quiniela.id}`}
                className="btn w-full text-center text-lg font-bold bg-kiniela-cta hover:bg-kiniela-ctaHover text-kiniela-text transition-colors"
              >
                Ver Detalles
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Info Banner */}
      {!loading && quinielas.length > 0 && (
        <div className="mt-8 bg-kiniela-card border border-kiniela-border rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-kiniela-muted flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-kiniela-muted">
              <p className="font-medium mb-1 text-kiniela-gold">¿Cómo funciona?</p>
              <ol className="list-decimal list-inside space-y-1 text-kiniela-muted">
                <li>Selecciona una quiniela y únete antes del cierre</li>
                <li>Predice el resultado de cada partido (Local/Empate/Visitante)</li>
                <li>Gana 1 punto por cada acierto</li>
                <li>Los 3 primeros lugares se llevan <span className="text-kiniela-gold">premios</span></li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

